import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UzBadge, UzButton } from '@utazzo/ui';
import { Check, X } from 'lucide-react';
import React, { useMemo } from 'react';
import { toast } from 'sonner';

import {
  approveWebsiteItem,
  bulkApproveWebsiteItems,
  bulkRejectWebsiteItems,
  listWebsiteApprovals,
  rejectWebsiteItem,
} from '../website-approvals.api';

import type {
  WebsiteApprovalEntityType,
  WebsiteApprovalItem,
  WebsiteApprovalRef,
  WebsiteApprovalsQuery,
  WebsiteBulkDecisionResult,
} from '@utazzo/shared';

import { DataGrid, type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useDataGridState } from '@/hooks/use-data-grid-state';
import { WEBSITE_PUBLISH_STATUS_LABEL, humanEnum } from '@/lib/enum-labels';

/**
 * Website content approvals - blogs / destination + category pages a
 * WEBSITE_SEO editor sent up for a manager decision.
 *
 * Built on PageShell + DataGrid so it matches the Product approvals and
 * lead-approvals inboxes exactly: same header typography, toolbar, card
 * chrome, skeleton/error/empty states, bulk actions and footer pagination.
 *
 * Review is opt-in - SEO keeps publish rights - so an empty queue is the
 * normal steady state, not a bug.
 */

const TYPE_LABEL: Record<WebsiteApprovalEntityType, string> = {
  BLOG: 'Blog',
  DESTINATION_PAGE: 'Destination page',
  CATEGORY_PAGE: 'Category page',
};

const EDIT_PATH: Record<WebsiteApprovalEntityType, (id: string) => string> = {
  BLOG: (id) => `/blogs/${id}`,
  DESTINATION_PAGE: (id) => `/destination-pages/${id}`,
  CATEGORY_PAGE: (id) => `/category-pages/${id}`,
};

/** '' is the "All types" option - mirrors the Type filter on product approvals. */
const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'DESTINATION_PAGE', label: 'Destination pages' },
  { value: 'CATEGORY_PAGE', label: 'Category pages' },
  { value: 'BLOG', label: 'Blogs' },
] as const;

/** Stable arrays - `useDataGridState` treats `filterIds` as a memo dependency. */
const FILTER_IDS = ['type'] as const;
const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'type',
    label: 'Type',
    options: TYPE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

const parseTypeFilter = (value: string | undefined): WebsiteApprovalsQuery['type'] =>
  value === 'BLOG' || value === 'DESTINATION_PAGE' || value === 'CATEGORY_PAGE' ? value : 'ALL';

const formatWhen = (iso: string | null): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const WebsiteApprovalsPage = (): React.JSX.Element => {
  const qc = useQueryClient();
  const gridState = useDataGridState({ filterIds: FILTER_IDS });
  const { state } = gridState;

  const query: WebsiteApprovalsQuery = {
    type: parseTypeFilter(state.filters.type),
    search: state.q || undefined,
    page: state.page,
    pageSize: state.pageSize,
  };
  const listQuery = useQuery({
    queryKey: ['website-approvals', 'list', query],
    queryFn: () => listWebsiteApprovals(query),
  });

  // A decision changes the record's publish status, so the dashboard
  // aggregate (tile counts, needs-attention) has to drop its cache too.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['website-approvals'] });
    void qc.invalidateQueries({ queryKey: ['dashboard', 'website-health'] });
  };

  const approve = useMutation({
    mutationFn: (i: { type: WebsiteApprovalEntityType; id: string }) =>
      approveWebsiteItem(i.type, i.id),
    onSuccess: () => {
      invalidate();
      toast.success('Approved and published');
    },
    onError: () => toast.error('Failed to approve'),
  });
  const reject = useMutation({
    mutationFn: (i: { type: WebsiteApprovalEntityType; id: string }) =>
      rejectWebsiteItem(i.type, i.id),
    onSuccess: () => {
      invalidate();
      toast.success('Sent back to draft');
    },
    onError: () => toast.error('Failed to reject'),
  });

  // Bulk decisions return partial success, so the toast reports both halves -
  // "12 approved" alone would hide the 3 that didn't take.
  const reportBulk = (verb: 'approved' | 'rejected') => (res: WebsiteBulkDecisionResult) => {
    invalidate();
    if (res.updated > 0) toast.success(`${res.updated} ${verb}`);
    if (res.failed.length > 0) toast.error(`${res.failed.length} could not be ${verb}`);
  };
  const bulkApprove = useMutation({
    mutationFn: (items: WebsiteApprovalRef[]) => bulkApproveWebsiteItems(items),
    onSuccess: reportBulk('approved'),
    onError: () => toast.error('Failed to approve the selected records'),
  });
  const bulkReject = useMutation({
    mutationFn: (items: WebsiteApprovalRef[]) => bulkRejectWebsiteItems(items),
    onSuccess: reportBulk('rejected'),
    onError: () => toast.error('Failed to reject the selected records'),
  });

  // `useMutation` returns a fresh object every render, so the columns memo
  // depends on the stable `mutate` fns + the pending flags instead.
  const approveMutate = approve.mutate;
  const rejectMutate = reject.mutate;
  const approvePending = approve.isPending;
  const rejectPending = reject.isPending;

  const rows = listQuery.data?.data ?? [];
  const total = listQuery.data?.pagination.totalItems ?? 0;
  const pagination = listQuery.data
    ? {
        page: listQuery.data.pagination.page,
        pageSize: listQuery.data.pagination.pageSize,
        totalItems: listQuery.data.pagination.totalItems,
      }
    : null;

  const columns = useMemo((): ReadonlyArray<DataGridColumn<WebsiteApprovalItem>> => {
    const cols: DataGridColumn<WebsiteApprovalItem>[] = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'title',
        header: 'Content',
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.title}</span>
            <span className="text-xs text-zinc-500">/{row.slug}</span>
          </div>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        width: '150px',
        cell: (row) => <span className="text-sm text-zinc-700">{TYPE_LABEL[row.entityType]}</span>,
      },
      {
        id: 'submittedBy',
        header: 'Submitted by',
        width: '170px',
        cell: (row) => (
          <span className="text-sm text-zinc-700">{row.submittedByName ?? 'Unknown'}</span>
        ),
      },
      {
        id: 'submittedAt',
        header: 'Submitted',
        width: '150px',
        cell: (row) => <span className="text-sm text-zinc-600">{formatWhen(row.submittedAt)}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        width: '120px',
        cell: (row) => (
          <UzBadge tone="warning" pill>
            {humanEnum(WEBSITE_PUBLISH_STATUS_LABEL, row.status)}
          </UzBadge>
        ),
      },
    ];

    cols.push({
      id: 'actions',
      header: '',
      width: '200px',
      align: 'right',
      truncate: false,
      resizable: false,
      alwaysVisible: true,
      cell: (row) => (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <UzButton
            size="sm"
            disabled={approvePending}
            onClick={() => approveMutate({ type: row.entityType, id: row.id })}
          >
            <Check className="mr-1 h-4 w-4" />
            Approve
          </UzButton>
          <UzButton
            size="sm"
            variant="outline"
            disabled={rejectPending}
            onClick={() => rejectMutate({ type: row.entityType, id: row.id })}
          >
            <X className="mr-1 h-4 w-4" />
            Reject
          </UzButton>
        </div>
      ),
    });

    return cols;
  }, [approveMutate, rejectMutate, approvePending, rejectPending, state.page, state.pageSize]);

  return (
    <PageShell
      title={total > 0 ? `Content approvals (${total} pending)` : 'Content approvals'}
      description="Blogs, destination pages and category pages an SEO editor has sent up for your sign-off. Review is optional, so this queue only fills when someone asks for it."
    >
      <DataGrid
        gridId="website-approvals"
        data={rows}
        columns={columns}
        filters={FILTERS}
        getRowId={(row) => `${row.entityType}-${row.id}`}
        onRowClick={(row) => window.location.assign(EDIT_PATH[row.entityType](row.id))}
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        pagination={pagination}
        state={state}
        onStateChange={gridState}
        searchPlaceholder="Search title or slug"
        emptyTitle="Nothing waiting on you"
        emptyDescription="Website content appears here when an SEO editor chooses Submit for review instead of publishing directly. They keep publish rights, so an empty queue is normal."
        tableLayout="auto"
        selectable
        // `getRowId` is composite (`TYPE-id`) because the queue spans three
        // website tables, so the bulk actions read `rows` - not `ids` - to
        // recover each record's type for the API.
        bulkActions={[
          {
            id: 'bulk-approve',
            label: 'Approve',
            icon: <Check className="h-4 w-4" />,
            onAction: (_ids, selected) => {
              void bulkApprove.mutateAsync(selected.map((r) => ({ type: r.entityType, id: r.id })));
            },
          },
          {
            id: 'bulk-reject',
            label: 'Reject',
            danger: true,
            icon: <X className="h-4 w-4" />,
            onAction: (_ids, selected) => {
              void bulkReject.mutateAsync(selected.map((r) => ({ type: r.entityType, id: r.id })));
            },
          },
        ]}
      />
    </PageShell>
  );
};
